import { NgModule, Provider } from '@angular/core';
import { Store } from '@ngrx/store';
import { MODULE_INITIALIZER, PageMetaResolver } from '@spartacus/core';
import { take } from 'rxjs/operators';
import { BaseSiteService } from '../../../core/site-context';
import { BuyerDataNormalizer } from './connector/buyer-data-normalizer';
import { CartModificationNormalizer } from './connector/cart-modification-normalizer';
import { CartNormalizer } from './connector/cart-normalizer';
import {
  BUYER_DATA_NORMALIZER,
  CART_MODIFICATION_NORMALIZER,
  CART_NORMALIZER,
  CUTTING_NORMALIZER,
  ORDER_CONDITION_NORMALIZER,
  ORDER_ENTRY_NORMALIZER,
  ORDER_ENTRY_SERIALIZER,
  PRE_PRINTED_LABEL_NORMALIZER,
  REEL_CUTTING_NORMALIZER,
  TINY_CART_NORMALIZER,
  UNIT_NORMALIZER,
} from './connector/converters';
import { CuttingNormalizer } from './connector/cutting-normalizer';
import { OrderConditionNormalizer } from './connector/order-condition-normalizer';
import { OrderEntryNormalizer } from './connector/order-entry-normalizer';
import { OrderEntrySerializer } from './connector/order-entry-serializer';
import { PrePrintedLabelNormalizer } from './connector/pre-printed-label-normalizer';
import { ReelCuttingNormalizer } from './connector/reel-cutting-normalizer';
import { TinyCartNormalizer } from './connector/tiny-cart-normalizer';
import { UnitNormalizer } from './connector/unit-normalizer';
import { CartEventModule } from './events/cart-event.module';
import {
  ActiveCartFacade,
  BuyerDataFacade,
  CartAccountingCodesFacade,
  CartFacade,
  CartPrePrintedLabelsFacade,
  CuttingFacade,
  FullPalletUpgradeFacade,
  ReelCuttingFacade,
  TinyCartFacade,
} from './facades';
import { CartValidatorFacade } from './facades/cart-validator.facade';
import { CartPageMetaResolver } from './resolvers/cart-page-meta.resolver';
import {
  ActiveCartService,
  BuyerDataService,
  CartAccountingCodesService,
  CartPrePrintedLabelsService,
  CartService,
  CuttingService,
  FullPalletUpgradeService,
  ReelCuttingService,
  TinyCartService,
} from './services';
import { CartValidatorService } from './services/cart-validator.service';
import { CartModuleLoaded } from './store/actions';
import { StateWithCart } from './store/cart-state';
import { CartStoreModule } from './store/cart-store.module';

export function cartBaseModuleInitFactory(store, baseSiteService: BaseSiteService): () => void {
  const result = () => {
    baseSiteService
      .getActive()
      .pipe(take(1))
      .subscribe(() => {
        store.dispatch(new CartModuleLoaded());
      });
  };
  return result;
}

const facadeProviders: Provider[] = [
  ActiveCartService,
  {
    provide: ActiveCartFacade,
    useExisting: ActiveCartService,
  },
  BuyerDataService,
  {
    provide: BuyerDataFacade,
    useExisting: BuyerDataService,
  },
  CartAccountingCodesService,
  {
    provide: CartAccountingCodesFacade,
    useExisting: CartAccountingCodesService,
  },
  CartPrePrintedLabelsService,
  {
    provide: CartPrePrintedLabelsFacade,
    useExisting: CartPrePrintedLabelsService,
  },
  CartService,
  {
    provide: CartFacade,
    useExisting: CartService,
  },
  CuttingService,
  {
    provide: CuttingFacade,
    useExisting: CuttingService,
  },
  FullPalletUpgradeService,
  {
    provide: FullPalletUpgradeFacade,
    useExisting: FullPalletUpgradeService,
  },
  ReelCuttingService,
  {
    provide: ReelCuttingFacade,
    useExisting: ReelCuttingService,
  },
  TinyCartService,
  {
    provide: TinyCartFacade,
    useExisting: TinyCartService,
  },
  CartValidatorService,
  {
    provide: CartValidatorFacade,
    useExisting: CartValidatorService,
  },
];

const metaResolvers: Provider[] = [
  {
    provide: PageMetaResolver,
    useExisting: CartPageMetaResolver,
    multi: true,
  },
];

const normalizerProviders: Provider[] = [
  {
    provide: ORDER_ENTRY_NORMALIZER,
    useClass: OrderEntryNormalizer,
    multi: true,
  },
  {
    provide: BUYER_DATA_NORMALIZER,
    useClass: BuyerDataNormalizer,
    multi: true,
  },
  {
    provide: CART_NORMALIZER,
    useClass: CartNormalizer,
    multi: true,
  },
  {
    provide: TINY_CART_NORMALIZER,
    useClass: TinyCartNormalizer,
    multi: true,
  },
  {
    provide: CART_MODIFICATION_NORMALIZER,
    useClass: CartModificationNormalizer,
    multi: true,
  },
  {
    provide: UNIT_NORMALIZER,
    useClass: UnitNormalizer,
    multi: true,
  },
  {
    provide: CUTTING_NORMALIZER,
    useClass: CuttingNormalizer,
    multi: true,
  },
  {
    provide: REEL_CUTTING_NORMALIZER,
    useClass: ReelCuttingNormalizer,
    multi: true,
  },
  {
    provide: PRE_PRINTED_LABEL_NORMALIZER,
    useClass: PrePrintedLabelNormalizer,
    multi: true,
  },
  {
    provide: ORDER_ENTRY_SERIALIZER,
    useClass: OrderEntrySerializer,
    multi: true,
  },
  {
    provide: ORDER_CONDITION_NORMALIZER,
    useClass: OrderConditionNormalizer,
    multi: true,
  },
  {
    provide: MODULE_INITIALIZER,
    useFactory: cartBaseModuleInitFactory,
    deps: [Store<StateWithCart>, BaseSiteService],
    multi: true,
  },
];

@NgModule({
  imports: [CartStoreModule, CartEventModule],
  providers: [...metaResolvers, ...facadeProviders, ...normalizerProviders],
})
export class CartBaseModule {}
